/**
 * Root application component.
 *
 * Sets up React Router (library mode), the AuthProvider context,
 * and all route definitions.  Protected routes are wrapped in Layout
 * (which provides the top navigation bar and <Outlet />).
 *
 * Secondary pages are lazy-loaded via React.lazy() so the initial
 * bundle only includes the landing page (ComparisonPage).  Chart-heavy
 * pages (Dashboard, Trend, Quality) pull Recharts into a separate
 * async chunk that is fetched on first navigation.
 *
 * Session 5.12: code-splitting for bundle optimisation.
 */
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// ---------------------------------------------------------------------------
// Eagerly loaded pages (landing page + auth)
// ---------------------------------------------------------------------------
import LoginPage from './pages/LoginPage';
import ComparisonPage from './pages/ComparisonPage';

// ---------------------------------------------------------------------------
// Lazy-loaded pages (loaded on first navigation)
// ---------------------------------------------------------------------------
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TrendPage = lazy(() => import('./pages/TrendPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const QualityPage = lazy(() => import('./pages/QualityPage'));
const RunReportPage = lazy(() => import('./pages/RunReportPage'));
const UploadPage = lazy(() => import('./pages/UploadPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

// ---------------------------------------------------------------------------
// Suspense fallback — matches the loading spinner used across the app
// ---------------------------------------------------------------------------

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="flex items-center gap-3 text-gray-400">
        <svg
          className="h-5 w-5 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2
               5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
               5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="text-sm">Loading&hellip;</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public route — no nav bar */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes — wrapped in Layout (nav bar + Outlet) */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ComparisonPage />} />
            <Route
              path="dashboard"
              element={
                <Suspense fallback={<PageFallback />}>
                  <DashboardPage />
                </Suspense>
              }
            />
            <Route
              path="trends/:sku"
              element={
                <Suspense fallback={<PageFallback />}>
                  <TrendPage />
                </Suspense>
              }
            />
            <Route
              path="changelog"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ChangelogPage />
                </Suspense>
              }
            />
            <Route
              path="quality"
              element={
                <Suspense fallback={<PageFallback />}>
                  <QualityPage />
                </Suspense>
              }
            />
            <Route
              path="run-report"
              element={
                <Suspense fallback={<PageFallback />}>
                  <RunReportPage />
                </Suspense>
              }
            />
            <Route
              path="upload"
              element={
                <Suspense fallback={<PageFallback />}>
                  <UploadPage />
                </Suspense>
              }
            />
            <Route
              path="admin"
              element={
                <ProtectedRoute requireAdmin>
                  <Suspense fallback={<PageFallback />}>
                    <AdminPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
